import React, { forwardRef } from "react";
import { Link } from "react-router-dom";
import { useMagnet } from "../../lib/interactive";
import { cn } from "../../lib/utils";

/**
 * The button: a slab, leaning the same four degrees as the highlighter.
 *
 * The lean is why it is not a pill any more. A skewed rounded-full shape has
 * its two ends pointing in different directions and reads as a rendering
 * fault; a small radius keeps it a marker stroke, which is the idea.
 *
 * The glyphs must not lean with it, so the children live in an inner span that
 * counter-skews. That inner span is also why `.slab > *` is styled rather than
 * the button itself — the flex row lives one level down now.
 *
 * Heights are 44/48/56. 44 is the touch floor and nothing goes under it,
 * including on desktop. Renders as a router Link, an anchor or a button
 * depending on what it is given.
 */

const VARIANTS = {
  /* The duotone. One of these per view, on the thing you most want clicked. */
  accent: "bg-accent text-void shadow-volt hover:shadow-brand-lg",
  solid: "bg-fg text-bg",
  ghost: "bg-surface-solid text-fg border border-line backdrop-blur-md hover:border-edge",
  quiet: "bg-transparent text-muted hover:text-fg",
};

const SIZES = {
  sm: "h-11 px-5 text-[13px]",
  md: "h-12 px-6 text-[13.5px]",
  lg: "h-14 px-8 text-[15px]",
};

const Action = forwardRef(function Action(
  { as, to, href, variant = "accent", size = "md", magnetic = true, className, children, ...rest },
  ref,
) {
  const Tag = as ?? (to ? Link : href ? "a" : "button");
  const isNative = Tag === "button";
  const magnet = useMagnet();

  return (
    <Tag
      ref={ref ?? (magnetic ? magnet : undefined)}
      to={to}
      href={href}
      type={isNative ? (rest.type ?? "button") : undefined}
      className={cn(
        "slab tap group inline-flex items-center justify-center font-bold whitespace-nowrap",
        magnetic && "magnet",
        "transition-[background-color,color,border-color,opacity,box-shadow] duration-200 ease-brand",
        "active:scale-[0.98]",
        "disabled:pointer-events-none disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:opacity-45",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      <span>{children}</span>
    </Tag>
  );
});

export default Action;
